import React, { useState } from "react";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../../../../designsystem/KC_Button/KC_Button";
import POSAFormPreview from "./POSAFormPreview";
import { tt } from "../../../../../../utils/i18n/i18n";
import {
  useAccountToDisplaySelector,
  updateCustomerThunk
} from "../../../../../../redux/actions/user";
import { setLoading } from "../../../../../../redux/actions/app";
import ChooseActivity from "./ChooseActivity";
import ChooseSupports from "./ChooseSupports";
import { POSAOptionType } from "@tootsweet/model/entities/UploadedPOSA";
import scrollToTop from "../../../../../../utils/helpers/scrollToTop";
import KC_Heading2 from "../../../../../../designsystem/typography/KC-Heading/KC_Heading2";
import TwemojiWrapper from "../../../../../../components/TwemojiWrapper/TwemojiWrapper";
import KC_UIText from "../../../../../../designsystem/typography/KC_UIText/KC_UIText";
import { KC_Weights } from "../../../../../../designsystem/ds_commons";
import KC_Input from "../../../../../../designsystem/KC_FormInput/KC_Input";
import ChooseClientPerDay from "./ChooseClientPerDay";

export default function POSAForm() {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const [step, setStep] = useState<number>(0);
  const [hasDineInService, setHasDineInService] = useState(false);
  const [hasDeliveryService, setHasDeliveryService] = useState(false);

  const [hasVisitedPLVMenu, setHavVisitedPLVMenu] = useState<boolean>(
    !!accountToDisplay?.businessUser?.hasVisitedPLVMenu
  );
  const [existingPOSA, setExistingPOSA] = useState<POSAOptionType[]>([]);
  const [tableSupports, setTableSupports] = useState<POSAOptionType[]>([]);
  const [deliveryBagSupports, setDeliveryBagSupports] = useState<
    POSAOptionType[]
  >([]);
  const [establishmentSupports, setEstablishmentSupports] = useState<
    POSAOptionType[]
  >([]);
  const [numberOfTables, setNumberOfTables] = useState<number | null>(null);
  const [deliveryClientsPerDay, setDeliveryClientsPerDay] =
    useState<string>("");

  const showEstablishmentSupports = !hasDeliveryService || hasDineInService;
  const isFormValid =
    (!showEstablishmentSupports || establishmentSupports.length > 0) &&
    (!hasDineInService || tableSupports.length > 0) &&
    (!hasDeliveryService || deliveryBagSupports.length > 0);

  const handleSubmit = async () => {
    dispatch(setLoading(true));

    try {
      dispatch(
        updateCustomerThunk(
          {
            posaFormAnswers: {
              existingPOSA: existingPOSA,
              tableSupports: tableSupports,
              deliveryBagSupports: deliveryBagSupports,
              establishmentSupports: establishmentSupports,
              numberOfTables: numberOfTables,
              deliveryClientsPerDay: deliveryClientsPerDay,
              hasDineInService,
              hasDeliveryService
            },
            hasVisitedPLVMenu: true
          },
          accountToDisplay?.trackingID,
          accountToDisplay?.token
        )
      );

      setHavVisitedPLVMenu(true);
      scrollToTop();
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  if (hasVisitedPLVMenu) {
    return (
      <POSAFormPreview
        onRetake={() => {
          setHasDeliveryService(false);
          setHasDineInService(false);
          setHavVisitedPLVMenu(false);
          setExistingPOSA([]);
          setTableSupports([]);
          setDeliveryBagSupports([]);
          setEstablishmentSupports([]);
          setNumberOfTables(null);
          setDeliveryClientsPerDay("");
          setStep(0);
        }}
      />
    );
  }

  return (
    <div className="posa-form-container d-flex align-items-center justify-content-center mt-auto mb-auto h-100">
      <Formik
        initialValues={{ support: [] as string[] }}
        onSubmit={handleSubmit}
      >
        {() => {
          return (
            <Form className="posa-form-card">
              <TwemojiWrapper isMediumText>
                <KC_Heading2
                  text={tt("posa_form_title")}
                  className="mb-5 text-center"
                />
              </TwemojiWrapper>

              {step == 0 && (
                <ChooseActivity
                  hasDeliveryService={hasDeliveryService}
                  hasDineInService={hasDineInService}
                  setHasDeliveryService={setHasDeliveryService}
                  setHasDineInService={setHasDineInService}
                  setStep={setStep}
                />
              )}

              {step == 1 && (
                <>
                  {hasDineInService && (
                    <ChooseSupports
                      id="table-support"
                      isSelectMultiple={false}
                      label={tt("posa_form_table_supports_question")}
                      selectOptions={TABLE_SUPPORT_OPTIONS}
                      selected={tableSupports}
                      setSelected={setTableSupports}
                    />
                  )}

                  {hasDineInService && (
                    <div className="mb-5">
                      <label className="mb-3">
                        <KC_UIText
                          weight={KC_Weights.md}
                          text={tt("posa_form_table_how_many")}
                        />
                      </label>
                      <KC_Input
                        id="number-of-tables"
                        htmlFor="numberOfTables"
                        type="number"
                        min={1}
                        placeholder={tt("posa_form_table_how_many")}
                        value={numberOfTables}
                        setFieldValue={(_field, value) =>
                          setNumberOfTables(value)
                        }
                      />
                    </div>
                  )}

                  {hasDeliveryService && (
                    <ChooseSupports
                      id="delivery-bag-support"
                      isSelectMultiple={false}
                      label={tt("posa_form_delivery_bag_supports_question")}
                      selectOptions={DELIVERY_BAG_SUPPORT_OPTIONS}
                      selected={deliveryBagSupports}
                      setSelected={setDeliveryBagSupports}
                    />
                  )}

                  {hasDeliveryService && (
                    <ChooseClientPerDay
                      id="delivery-clients-per-day"
                      label={tt("posa_form_delivery_clients_per_day")}
                      selected={deliveryClientsPerDay}
                      setSelected={setDeliveryClientsPerDay}
                    />
                  )}

                  {showEstablishmentSupports && (
                    <ChooseSupports
                      id="support-list"
                      isSelectMultiple
                      label={tt("posa_form_which_supports_do_you_use")}
                      selectOptions={EXISTING_POSA_OPTIONS}
                      selected={existingPOSA}
                      setSelected={setExistingPOSA}
                    />
                  )}

                  {showEstablishmentSupports && (
                    <ChooseSupports
                      id="establishment-support"
                      isSelectMultiple
                      label={tt("posa_form_establishment_support_question")}
                      selectOptions={ESTABLISHMENT_SUPPORT_OPTIONS}
                      selected={establishmentSupports}
                      setSelected={setEstablishmentSupports}
                    />
                  )}

                  <div className="d-flex justify-content-end mt-5">
                    <KC_Button
                      id="posa-form-submit"
                      size={KC_ButtonSize.Large}
                      type={KC_ButtonType.Submit}
                      text={tt("posa_list_next")}
                      disabled={!isFormValid}
                    />
                  </div>
                </>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export const EXISTING_POSA_OPTIONS: POSAOptionType[] = [
  {
    format: "table_set",
    displayName: tt("utm_source_placemat"),
    isUpload: true
  },
  {
    format: "paper_menu",
    displayName: tt("posa_item_paper_menu_display"),
    isUpload: true
  },
  {
    format: "print_on_packaging",
    displayName: tt("posa_form_support_packaging_imprimables"),
    isUpload: true
  },
  {
    format: "print_on_bag",
    displayName: tt("posa_form_delivery_print_on_bag"),
    isUpload: true
  },
  { format: "none", displayName: tt("posa_form_none") }
];

const TABLE_SUPPORT_OPTIONS: POSAOptionType[] = [
  { format: "stickers_dome", displayName: tt("posa_form_table_stickers_dome") },
  { format: "stickers10x10", displayName: tt("posa_form_table_stickers10x10") },
  { format: "flyer_a5", displayName: tt("posa_item_a5_table_tent_display") },
  { format: "flyer_a6", displayName: tt("posa_item_a6_table_tent_display") },
  { format: "none", displayName: tt("posa_form_table_no_game") }
];

export const DELIVERY_BAG_SUPPORT_OPTIONS: POSAOptionType[] = [
  {
    format: "flyer_a6",
    displayName: tt("posa_form_delivery_staple_flyer")
  },
  {
    format: "stickers10x10",
    displayName: tt("posa_form_delivery_stick_sticker")
  },
  {
    format: "businesscardsstandard",
    displayName: tt("posa_form_delivery_staple_business_card")
  },
  {
    format: "none",
    displayName: tt("posa_form_delivery_no_game")
  }
];

const ESTABLISHMENT_SUPPORT_OPTIONS: POSAOptionType[] = [
  { format: "poster_a2", displayName: tt("posa_form_establishment_poster_a2") },
  { format: "poster_a3", displayName: tt("utm_source_a3_poster") },
  { format: "flyer_a6", displayName: tt("posa_item_a6_display_display") },
  { format: "kakemono", displayName: tt("posa_item_kakemono_display") },
  { format: "stickers10x10", displayName: tt("posa_form_table_stickers10x10") },
  {
    format: "stickers_dome",
    displayName: tt("posa_form_establishment_sticker_dome")
  },
  { format: "none", displayName: tt("posa_form_establishment_none") }
];
